/**
 * Diagnostics for "clicking a product card or its Add to cart does nothing".
 *
 * Watches presses on product-card links and Add to cart buttons and reports the ones
 * that fail, never the ones that work. A press that moves more than Flickity's drag
 * threshold is a carousel swipe and is ignored. Outcomes:
 *  - cancelled:      something called preventDefault on the click;
 *  - covered:        the press landed on the carousel over the target, and the click
 *                    never reached it;
 *  - no_navigation:  a product-link click went through but the page stayed put;
 *  - no_click:       press and release landed on the target but no click followed.
 *
 * Every failure goes to GA4 as `odbm_click_failed` (once per kind per page view; GA4
 * applies Consent Mode). Opening any page with `?odbm_debug=clicks` (remembered until
 * `?odbm_debug=off`) also logs each failure to the console, with the stack of whatever
 * cancelled the click.
 */

const ODBM_CLICK_DEBUG_PARAM = "odbm_debug";
const ODBM_CLICK_DEBUG_TARGETS =
  '.product-card a[href*="/products/"], .product-card button[name="add"]';
const ODBM_CLICK_DEBUG_PREFIX = "[odbm-click-debug]";
const ODBM_CLICK_GA_EVENT = "odbm_click_failed";
const ODBM_CLICK_WAIT_MS = 300;
const ODBM_NAV_WAIT_MS = 3000;
/** Flickity treats a press that moves more than this as a drag. */
const ODBM_DRAG_THRESHOLD_PX = 3;
const WHITESPACE_RE = /\s+/;
const FRAME_PREFIX_RE = /^at /;

/**
 * Reads the opt-in flag, applying `?odbm_debug=clicks|off` to storage first.
 * @param {string} search - location.search
 * @param {() => Storage} getStorage - throws or returns null where storage is blocked
 */
const odbmClickDebugEnabled = (search, getStorage) => {
  const param = new URLSearchParams(search).get(ODBM_CLICK_DEBUG_PARAM);
  try {
    const storage = getStorage();
    if (param === "clicks") {
      storage.setItem(ODBM_CLICK_DEBUG_PARAM, "clicks");
    } else if (param === "off") {
      storage.removeItem(ODBM_CLICK_DEBUG_PARAM);
    }
    return storage.getItem(ODBM_CLICK_DEBUG_PARAM) === "clicks";
  } catch {
    return param === "clicks";
  }
};

const odbmDescribe = (el) => {
  if (el?.nodeType !== 1) {
    return String(el);
  }
  let text = el.tagName.toLowerCase();
  if (el.id) {
    text += `#${el.id}`;
  }
  const classes =
    typeof el.className === "string"
      ? el.className.trim().split(WHITESPACE_RE)
      : [];
  if (classes[0]) {
    text += `.${classes.slice(0, 3).join(".")}`;
  }
  return text;
};

/** The first few frames above the wrapper that captured `stack`, as one line. */
const odbmCaller = (stack) =>
  String(stack)
    .split("\n")
    .slice(2, 6)
    .map((line) => line.trim().replace(FRAME_PREFIX_RE, ""))
    .join(" <- ") || "(no stack)";

/**
 * GA4 parameters for a failed press. Values are strings so they read cleanly as
 * custom dimensions.
 */
const odbmClickTargetType = (target) => {
  if (target.matches("a")) {
    return "product_link";
  }
  return target.matches("button") ? "add_to_cart" : "carousel";
};

const odbmClickGaParams = (kind, press) => {
  const productLink = press.target.matches("a")
    ? press.target
    : press.target
        .closest(".product-card")
        ?.querySelector('a[href*="/products/"]');
  return {
    failure_kind: kind,
    target_type: odbmClickTargetType(press.target),
    in_flickity: press.target.closest(".flickity-enabled") ? "yes" : "no",
    flickity_dragging: press.flickityDragging ? "yes" : "no",
    pointer_type: press.pointerType || "unknown",
    link_url: productLink?.getAttribute("href") || undefined,
  };
};

class ODBMClickDebug {
  constructor(win, { verbose = false } = {}) {
    this.win = win;
    this.verbose = verbose;
    this.press = null;
    this.leaving = false;
    this.sentKinds = new Set();
    if (verbose) {
      this.wrapPreventDefault();
      win.console.info(
        `${ODBM_CLICK_DEBUG_PREFIX} on: logging failed product clicks. Turn off with ?${ODBM_CLICK_DEBUG_PARAM}=off`
      );
    }
    win.addEventListener("pointerdown", (e) => this.onPointerDown(e), true);
    win.addEventListener("pointerup", (e) => this.onPointerUp(e), true);
    win.addEventListener("click", (e) => this.onClick(e), true);
    win.addEventListener("pagehide", () => {
      this.leaving = true;
    });
    win.addEventListener("beforeunload", () => {
      this.leaving = true;
    });
  }

  wrapPreventDefault() {
    const self = this;
    const original = this.win.Event.prototype.preventDefault;
    this.win.Event.prototype.preventDefault = function (...args) {
      if (this.type === "click" && self.press) {
        self.press.cancelledBy.push(
          odbmCaller(new Error("odbm-click-debug caller").stack)
        );
      }
      return original.apply(this, args);
    };
  }

  onPointerDown(e) {
    if (e.button !== 0) {
      return;
    }
    this.press = null;
    // Runs before Flickity's own pointerdown, so this class is left over from a press
    // that never ended. A stuck carousel follows the pointer, so no card may be under it.
    const pressedViewport = e.target.closest?.(".flickity-viewport");
    const stuckViewport = pressedViewport?.classList.contains("is-pointer-down")
      ? pressedViewport
      : null;
    const target =
      e.target.closest?.(ODBM_CLICK_DEBUG_TARGETS) ||
      this.targetUnder(e) ||
      stuckViewport;
    if (!target) {
      return;
    }
    const viewport = target.closest(".flickity-viewport");
    this.press = {
      target,
      downEl: e.target,
      down: odbmDescribe(e.target),
      x: e.clientX,
      y: e.clientY,
      pointerType: e.pointerType,
      flickityDragging: Boolean(
        viewport?.classList.contains("is-pointer-down")
      ),
      cancelledBy: [],
      clicked: false,
    };
  }

  /**
   * A target under the press point when the press landed on the carousel rather than
   * a card. Card content outside a target (price, badges) is skipped: not navigating
   * from there is the vendor's design.
   */
  targetUnder(e) {
    const root = e.target.closest?.(".product-card")
      ? null
      : e.target.closest?.(".flickity-viewport");
    return root
      ? this.win.document
          .elementsFromPoint?.(e.clientX, e.clientY)
          ?.find(
            (el) => root.contains(el) && el.matches(ODBM_CLICK_DEBUG_TARGETS)
          )
      : null;
  }

  onPointerUp(e) {
    const press = this.press;
    if (!press) {
      return;
    }
    press.up = odbmDescribe(e.target);
    press.moved = Math.max(
      Math.abs(e.clientX - press.x),
      Math.abs(e.clientY - press.y)
    );
    if (press.moved > ODBM_DRAG_THRESHOLD_PX) {
      this.finish(press);
      return;
    }
    const releasedOnPress =
      press.target.contains(e.target) || press.downEl === e.target;
    this.win.setTimeout(() => {
      if (!press.clicked) {
        if (releasedOnPress) {
          this.report("no_click", press);
        }
        this.finish(press);
      }
    }, ODBM_CLICK_WAIT_MS);
  }

  onClick(e) {
    const press = this.press;
    if (!press) {
      return;
    }
    press.clicked = true;
    press.click = odbmDescribe(e.target);
    const reachedTarget = press.target.contains(e.target);
    // Let every other listener run before judging the outcome.
    this.win.setTimeout(() => {
      if (e.defaultPrevented) {
        this.report("cancelled", press);
        this.finish(press);
        return;
      }
      if (!reachedTarget) {
        this.report("covered", press);
        this.finish(press);
        return;
      }
      const opensElsewhere =
        !press.target.matches("a") ||
        press.target.target === "_blank" ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey;
      if (opensElsewhere) {
        this.finish(press);
        return;
      }
      this.win.setTimeout(() => {
        if (!this.leaving) {
          this.report("no_navigation", press);
        }
        this.finish(press);
      }, ODBM_NAV_WAIT_MS);
    }, 0);
  }

  finish(press) {
    if (this.press === press) {
      this.press = null;
    }
  }

  report(kind, press) {
    const params = odbmClickGaParams(kind, press);
    if (!this.sentKinds.has(kind) && typeof this.win.gtag === "function") {
      this.sentKinds.add(kind);
      this.win.gtag("event", ODBM_CLICK_GA_EVENT, params);
    }
    if (!this.verbose) {
      return;
    }
    const fields = {
      ...params,
      target: odbmDescribe(press.target),
      down: press.down,
      up: press.up,
      click: press.click,
      moved: press.moved === undefined ? undefined : `${press.moved}px`,
      cancelled_by: press.cancelledBy?.join(" || ") || undefined,
      page: this.win.location.pathname,
    };
    const line = Object.entries(fields)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => `${key}=${value}`)
      .join(" | ");
    this.win.console.warn(`${ODBM_CLICK_DEBUG_PREFIX} ${line}`);
  }
}

new ODBMClickDebug(window, {
  verbose: odbmClickDebugEnabled(
    window.location.search,
    () => window.localStorage
  ),
});
